import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { SummaryCard } from "../components/home/SummaryCard";
import { UpcomingBills } from "../components/home/UpcomingBills";
import { IncomeVsExpensesChart } from "../components/home/IncomeVsExpensesChart";
import { PendingPayments } from "../components/home/PendingPayments";
import { MonthlyHistoryTable } from "../components/home/MonthlyHistoryTable";
import { MonthlyHistoryCards } from "../components/home/MonthlyHistoryCards";
import { useControlCash } from "../store/controlCashStore";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useConfirm } from "../contexts/ConfirmContext";

const calcChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const {
    dispatch,
    state,
    getMonthTotals,
    getMonthExpenses,
    getUpcomingBills,
    getMonthsSortedByDate,
    formatDisplayAmount,
    isDataLoading,
    dataError
  } = useControlCash();
  const { confirm } = useConfirm();

  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currencySymbol = state.currencySymbol ?? "S/";
  const formatAmount = formatDisplayAmount;

  const handleDeleteMonth = async (e, month) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Eliminar mes",
      message: `¿Estás seguro de eliminar "${month.name}"?\n\nSe eliminarán también los ingresos y gastos de este mes.`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      dispatch({ type: "DELETE_MONTH", payload: month.id });
    }
  };

  const sortedMonths = getMonthsSortedByDate();

  const months = sortedMonths.map((month) => {
    const totals = getMonthTotals(month.id);
    const pendingExpenses = getMonthExpenses(month.id).filter((e) => e.status === "PENDING");
    return {
      id: month.id,
      name: month.name,
      year: month.year,
      monthNumber: month.monthNumber,
      ingresos: totals.income,
      gastos: totals.totalCommitted ?? totals.expenses,
      ahorro: totals.savings,
      restante: totals.availableRemaining ?? totals.remaining,
      pendientes: pendingExpenses.length,
      pendingExpenses,
      totalPending: totals.totalPending ?? 0
    };
  });

  const currentMonth = months[0] ?? null;
  const previousMonth = months[1] ?? null;

  const currentMonthData = currentMonth
    ? {
        income: {
          amount: currentMonth.ingresos,
          change: calcChange(currentMonth.ingresos, previousMonth?.ingresos)
        },
        expenses: {
          amount: currentMonth.gastos,
          change: calcChange(currentMonth.gastos, previousMonth?.gastos)
        },
        savings: {
          amount: currentMonth.ahorro,
          change: calcChange(currentMonth.ahorro, previousMonth?.ahorro)
        },
        balance: {
          amount: currentMonth.restante,
          change: calcChange(currentMonth.restante, previousMonth?.restante)
        }
      }
    : null;

  const upcomingBills = getUpcomingBills();
  const shortMonthName = (name) => {
    if (!name) return "";
    const firstWord = name.split(" ")[0] || "";
    return firstWord.length >= 3 ? firstWord.slice(0, 3) : firstWord;
  };

  const chartDataFormatted = months
    .slice(0, 6)
    .reverse()
    .map((m) => ({
      month: shortMonthName(m.name),
      income: m.ingresos,
      expenses: m.gastos
    }));

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Welcome */}
        <div className="mb-10 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
            Bienvenido, {user?.name?.split(" ")[0] || "Usuario"}
          </h1>
          <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
            {currentMonth
              ? `Resumen financiero de ${currentMonth.name}`
              : "Resumen financiero"}
          </p>
        </div>

        {dataError && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
            Error al cargar datos: {dataError}
          </div>
        )}

        {isDataLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
          </div>
        ) : !currentMonth ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-12 text-center mb-8">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No hay meses registrados</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Crea tu primer mes para comenzar a controlar tus finanzas</p>
            <button
              onClick={() => navigate("/month/new")}
              className="inline-flex items-center gap-2 px-10 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuevo Mes
            </button>
          </div>
        ) : (
          <>
            {isMobile ? (
              /* Mobile layout - 4 cards en 2 columnas */
              <>
                <section className="mb-8">
                  <div className="grid grid-cols-2 gap-3">
                    <SummaryCard
                      title="Balance Disponible"
                      amount={formatAmount(currentMonthData.balance.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.balance.change}
                      color="blue"
                    />
                    <SummaryCard
                      title="Ingresos del Mes"
                      amount={formatAmount(currentMonthData.income.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.income.change}
                      color="green"
                    />
                    <SummaryCard
                      title="Gastos del Mes"
                      amount={formatAmount(currentMonthData.expenses.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.expenses.change}
                      color="red"
                    />
                    <SummaryCard
                      title="Ahorro del Mes"
                      amount={formatAmount(currentMonthData.savings.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.savings.change}
                      color="orange"
                    />
                  </div>
                </section>

                <section className="mb-8">
                  <UpcomingBills
                    bills={upcomingBills}
                    formatAmount={formatAmount}
                    onBillClick={(b) => b.monthId && navigate(`/month/${b.monthId}`)}
                  />
                </section>

                <section className="mb-8">
                  <IncomeVsExpensesChart
                    data={chartDataFormatted}
                    formatAmount={formatAmount}
                    isDark={isDark}
                  />
                </section>

                <section className="mb-8">
                  <PendingPayments
                    expenses={currentMonth.pendingExpenses}
                    formatAmount={formatAmount}
                    totalPending={currentMonth.totalPending}
                    monthId={currentMonth.id}
                    monthYear={currentMonth.year}
                    monthNumber={currentMonth.monthNumber}
                    customCategories={state.customCategories || []}
                    defaultExpanded={false}
                  />
                </section>

                <section className="mb-8">
                  <MonthlyHistoryCards
                    history={months}
                    formatAmount={formatAmount}
                    onDeleteMonth={handleDeleteMonth}
                  />
                </section>

                <section className="pb-6">
                  <button
                    onClick={() => navigate("/month/new")}
                    className="w-full inline-flex items-center justify-center gap-2 px-8 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 transition-all text-base"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Nuevo Mes
                  </button>
                </section>
              </>
            ) : (
              /* Desktop layout */
              <>
                <section className="mb-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <SummaryCard
                      title="Ingresos del Mes"
                      amount={formatAmount(currentMonthData.income.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.income.change}
                      color="green"
                    />
                    <SummaryCard
                      title="Gastos del Mes"
                      amount={formatAmount(currentMonthData.expenses.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.expenses.change}
                      color="red"
                    />
                    <SummaryCard
                      title="Ahorro del Mes"
                      amount={formatAmount(currentMonthData.savings.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.savings.change}
                      color="orange"
                    />
                    <SummaryCard
                      title="Balance Disponible"
                      amount={formatAmount(currentMonthData.balance.amount)}
                      currency={currencySymbol}
                      change={currentMonthData.balance.change}
                      color="blue"
                    />
                  </div>
                </section>

                <section className="mb-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <UpcomingBills
                      bills={upcomingBills}
                      formatAmount={formatAmount}
                      onBillClick={(b) => b.monthId && navigate(`/month/${b.monthId}`)}
                    />
                    <IncomeVsExpensesChart
                      data={chartDataFormatted}
                      formatAmount={formatAmount}
                      isDark={isDark}
                    />
                  </div>
                </section>

                <section className="mb-12">
                  <PendingPayments
                    expenses={currentMonth.pendingExpenses}
                    formatAmount={formatAmount}
                    totalPending={currentMonth.totalPending}
                    monthId={currentMonth.id}
                    monthYear={currentMonth.year}
                    monthNumber={currentMonth.monthNumber}
                    customCategories={state.customCategories || []}
                    defaultExpanded={false}
                  />
                </section>

                <section className="mb-12">
                  <MonthlyHistoryTable
                    history={months}
                    formatAmount={formatAmount}
                    onDeleteMonth={handleDeleteMonth}
                  />
                </section>

                <section className="flex justify-center pt-4 pb-8">
                  <button
                    onClick={() => navigate("/month/new")}
                    className="inline-flex items-center gap-2 px-10 py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all text-base"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Nuevo Mes
                  </button>
                </section>
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Home;
